/**
 * Argus Live — shared Zod schemas for cross-package validation.
 *
 * Single source of truth for the request/response shapes used by:
 *   - @argusai/api    (Zod validation on Argus Live API routes — AL-A2)
 *   - @argusai/web    (form validation in the authoring UI — AL-A3)
 *   - @argusai/ai-core (runtime binding manifest validation — AL-A3)
 *
 * See docs/ARGUS-LIVE-SPEC.md §3 (DB schema) + §5 (RBAC) + §8 (API surface).
 *
 * Naming convention: capitalised PascalCase Schema names match the Zod
 * idiom used elsewhere in this package (RbacResourceSchema,
 * ClearanceLevelSchema, etc.).
 */

import { z } from 'zod';
import { ExportControlSchema, SensitivitySchema } from './document.schema.js';

// ─── Lifecycle (matches DB CHECK chk_tenant_dashboards_status) ─────────

/**
 * 3-state lifecycle for tenant_dashboards.
 *   - draft     — author is editing; not visible to non-grantees
 *   - published — visible to grantees per tenant_dashboard_grants
 *   - archived  — read-only; preserved for audit but excluded from listings
 */
export const DashboardStatusSchema = z.enum([
  'draft',
  'published',
  'archived',
]);
export type DashboardStatusValue = z.infer<typeof DashboardStatusSchema>;

// ─── Grants (matches DB CHECK chk_tenant_dashboard_grants_role) ────────

/**
 * Access levels carried on tenant_dashboard_grants.role. Ordered low → high:
 * view < operator < edit < admin. 'operator' (Cockpit Phase 2, c08) ranks
 * above view and below edit — view + (Phase 3) execute/approve, but NOT edit
 * layout. Mirrors DASHBOARD_GRANT_ROLES in
 * packages/api/src/db/schema/argus-live-grants.ts and the DB CHECK
 * tenant_dashboard_grants_role_vocab_v2.
 */
export const DashboardGrantAccessSchema = z.enum([
  'view',
  'operator',
  'edit',
  'admin',
]);
export type DashboardGrantAccess = z.infer<typeof DashboardGrantAccessSchema>;

/**
 * Built-in tenant role values when grantee_role is non-null. Matches
 * packages/shared/src/schemas/permissions.ts RoleIdSchema for the
 * tenant-side roles (no `power_user` per W1a-27).
 */
export const DashboardGrantTenantRoleSchema = z.enum([
  'owner',
  'admin',
  'member',
  'viewer',
]);
export type DashboardGrantTenantRoleValue = z.infer<
  typeof DashboardGrantTenantRoleSchema
>;

// ─── Classification cache shape (jsonb column) ─────────────────────────

/**
 * 2-axis classification per CLAUDE.md §11.3. Mirrored on the
 * `classification_tag` jsonb column on both tenant_dashboards and
 * tenant_dashboard_versions for fast listing filters. The authoritative
 * source at runtime is the per-binding re-check; this is the cached
 * "highest across the snapshot" value at publish time.
 */
export const DashboardClassificationSchema = z.object({
  sensitivity: SensitivitySchema,
  exportControl: ExportControlSchema,
});
export type DashboardClassification = z.infer<
  typeof DashboardClassificationSchema
>;

// ─── Cockpit binding source kinds (Cockpit Phase 1, §2.B) ─────────────

/**
 * The seven source kinds a binding can declare. A binding without a
 * `source` field is treated as `mcp_tool` (legacy / backward-compat).
 *
 *   Gateway read kinds (dispatch through the MCP gateway via toolSlug):
 *     - library     — Library document/connector reads surfaced as a tool
 *     - mcp_tool    — a raw MCP tool call (the legacy default)
 *     - connection  — a Connections-rail external integration read
 *     - workflow    — a read against a bound workflow's output
 *
 *   Local read kinds (carry a tableSlug instead of a toolSlug):
 *     - tables        — durable Tables rows (read an arbitrary tenant table)
 *     - cockpit_state — the cockpit's OWN durable/per-user STATE store (Phase 4,
 *                       READY). Carries a `tableSlug` AND a `stateColumns`
 *                       allow-list of the columns the cockpit may write. The
 *                       table is cloned into the tenant on PUBLISH
 *                       (`cloneStateTablesForPublish`) so the first WRITE_STATE
 *                       targets a tenant row (dodges the bSP3b canonical-write
 *                       trap), and the STATE route enforces `stateColumns` on
 *                       every write (defense-in-depth over the open jsonb store).
 *
 *   Action (NOT readable via data-fetch — executes via the write path in
 *   Cockpit Phase 3):
 *     - action      — carries an actionId, rejected by the read router
 *
 * The `kind` on a gateway read is a SEMANTIC LABEL only — it drives the
 * future binding picker + is informational. Dispatch is identical across
 * all four gateway kinds (and the legacy no-source path): the binding's
 * top-level `toolSlug` is sent to `callGatewayWithTimeout`.
 */
export const BindingSourceKindSchema = z.enum([
  'library',
  'mcp_tool',
  'connection',
  'workflow',
  'tables',
  'cockpit_state',
  'action',
]);
export type BindingSourceKind = z.infer<typeof BindingSourceKindSchema>;

/**
 * The four source kinds that collapse to a gateway dispatch via the
 * binding's top-level `toolSlug`. Used by the router + by the binding
 * superRefine to require a valid toolSlug. Exported for reuse by the
 * server router (`resolveBindingSource`) and the web authoring validator
 * so all three validators agree on the partition.
 */
export const GATEWAY_SOURCE_KINDS = [
  'library',
  'mcp_tool',
  'connection',
  'workflow',
] as const satisfies readonly BindingSourceKind[];

/**
 * A binding source-kind set. True iff `kind` dispatches through the MCP
 * gateway (and therefore requires the binding's top-level `toolSlug`).
 */
export function isGatewaySourceKind(
  kind: BindingSourceKind,
): kind is (typeof GATEWAY_SOURCE_KINDS)[number] {
  return (GATEWAY_SOURCE_KINDS as readonly string[]).includes(kind);
}

/**
 * Cockpit action target kinds (Phase 3, §2.C). An `action` binding either
 * dispatches an MCP write tool through the gateway (`mcp_tool`) or launches
 * a bound Automate-rail workflow (`workflow`). The cockpit BINDS workflows,
 * it never authors them — `workflowId` references an existing automation.
 */
export const CockpitActionKindSchema = z.enum(['mcp_tool', 'workflow']);
export type CockpitActionKind = z.infer<typeof CockpitActionKindSchema>;

/**
 * Per-action approval gating (Phase 3, §2.C — the execution-authorization
 * axis). Mirrors the `ExecutionAuthorization.mode` vocabulary verbatim:
 *   - read_only          — the button is disabled / informational; the
 *                          execute endpoint refuses with ACTION_FORBIDDEN.
 *   - approval_required  — the click mints an `agent_tool_approvals` row
 *                          (gate_kind='cockpit_action'); a DIFFERENT user
 *                          must approve before the write runs (default).
 *   - auto_execute       — the click mints a server-side authorization and
 *                          dispatches immediately (mcp_tool → gateway,
 *                          workflow → run/enqueue with the workflow_auto
 *                          grant). Still re-validated fail-closed by the
 *                          gateway resolver.
 */
export const CockpitActionGatingSchema = z.enum([
  'read_only',
  'approval_required',
  'auto_execute',
]);
export type CockpitActionGating = z.infer<typeof CockpitActionGatingSchema>;

/**
 * The optional `source` discriminator on a binding (Cockpit Phase 1, extended
 * for the executable `action` variant in Phase 3 §2.C).
 *
 *   - Gateway kinds (library/mcp_tool/connection/workflow): carry neither
 *     tableSlug nor actionId — they reuse the binding's top-level toolSlug.
 *   - `tables`: carries a `tableSlug` (reads an arbitrary tenant Table).
 *   - `cockpit_state` (Phase 4 — READY): carries a `tableSlug` AND a non-empty
 *     `stateColumns` allow-list — the only columns the cockpit may WRITE into
 *     its own-state Table. The STATE route + tenant-table service reject a write
 *     touching any key outside this list (the physical store is open jsonb, so
 *     the allow-list is the authored contract that constrains it).
 *   - `action` (Phase 3): carries an `actionId` plus the action TARGET +
 *     gating. `actionKind='mcp_tool'` requires a namespaced `toolSlug`;
 *     `actionKind='workflow'` requires a `workflowId` UUID. Every usable
 *     action declares `approvalGating`. NOT readable here — executed via the
 *     write path (POST …/actions/:actionId/execute).
 *
 * Backward-compat note (Phase 1 minimal `{kind:'action', actionId}`): the
 * Phase-1 shape was never executable. Phase 3 makes `actionKind` +
 * `approvalGating` REQUIRED for a USABLE action binding. The fields validate
 * only when present at this schema layer (so a legacy Phase-1 manifest still
 * parses for read-router rejection), but the execute-action endpoint REJECTS
 * an action missing them with ACTION_NOT_EXECUTABLE — the schema and the
 * executor together make the contract whole.
 *
 * The superRefine enforces the kind ↔ field constraints. The binding-level
 * `.refine()` (below) additionally requires a valid top-level toolSlug for
 * gateway/legacy bindings.
 */
export const DashboardBindingSourceSchema = z
  .object({
    kind: BindingSourceKindSchema,
    /**
     * Required for `tables` / `cockpit_state`. Matches the `tenant_tables`
     * slug vocabulary (lowercase letters/digits/underscores, starts with a
     * letter). Forbidden for gateway kinds + action.
     */
    tableSlug: z
      .string()
      .min(1)
      .max(120)
      .regex(
        /^[a-z][a-z0-9_]*$/,
        'tableSlug must be a tenant_tables slug (lowercase, digits, underscores; starts with a letter)',
      )
      .optional(),
    /**
     * Phase 4 (STATE) — REQUIRED for `cockpit_state`, FORBIDDEN for every other
     * kind. The allow-list of column (top-level jsonb key) names this binding is
     * permitted to WRITE into its own-state Table. The STATE route + the
     * tenant-table service reject any WRITE_STATE whose `data` carries a key
     * outside this set (the physical store is an OPEN jsonb map, so this authored
     * allow-list is the contract that constrains it — a cockpit can only mutate
     * the state columns it declared at author time). 1-64 distinct columns; each
     * a lowercase identifier (letters/digits/underscores, starts with a letter).
     */
    stateColumns: z
      .array(
        z
          .string()
          .min(1)
          .max(120)
          .regex(
            /^[a-z][a-z0-9_]*$/,
            'stateColumns[*] must be a column name (lowercase, digits, underscores; starts with a letter)',
          ),
      )
      .min(1)
      .max(64)
      .optional(),
    /**
     * Required for `action`. Stable identifier of the cockpit action that
     * the write path (Phase 3) will execute. Forbidden for read kinds.
     */
    actionId: z.string().min(1).max(120).optional(),
    /**
     * Phase 3 — the action TARGET kind. Present (and required for a usable
     * action) when kind='action'. Forbidden for every read kind.
     */
    actionKind: CockpitActionKindSchema.optional(),
    /**
     * Phase 3 — namespaced MCP write-tool slug. Present when
     * actionKind='mcp_tool'. Same 3-tier namespace regex as a read binding.
     */
    toolSlug: z
      .string()
      .min(1)
      .max(200)
      .regex(
        /^(iq|ext|forge)\.[a-zA-Z0-9_-]+\.[a-zA-Z_][a-zA-Z0-9_]*$/,
        'source.toolSlug must be a namespaced slug (e.g. ext.maximo.createWorkOrder)',
      )
      .optional(),
    /**
     * Phase 3 — bound workflow id. Present when actionKind='workflow'. The
     * cockpit binds (never authors) an existing Automate-rail workflow.
     */
    workflowId: z.string().uuid().optional(),
    /**
     * Phase 3 — per-action gating. Required for a usable action binding.
     */
    approvalGating: CockpitActionGatingSchema.optional(),
    /** Phase 3 — optional button label for the action component. */
    buttonLabel: z.string().min(1).max(120).optional(),
  })
  .superRefine((s, ctx) => {
    if (s.kind === 'tables' || s.kind === 'cockpit_state') {
      if (typeof s.tableSlug !== 'string' || s.tableSlug.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `source.kind='${s.kind}' requires a tableSlug`,
          path: ['tableSlug'],
        });
      }
      for (const k of ['actionId', 'actionKind', 'toolSlug', 'workflowId', 'approvalGating', 'buttonLabel'] as const) {
        if (s[k] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `source.kind='${s.kind}' must not carry '${k}'`,
            path: [k],
          });
        }
      }
      // Phase 4 — `stateColumns` is the WRITE allow-list for the cockpit's own
      // state Table. It is REQUIRED for `cockpit_state` (so a published cockpit
      // can never write a key it did not author) and FORBIDDEN for plain
      // `tables` (a read-only arbitrary-table binding has no write contract). We
      // also reject duplicate column names so the allow-list is a clean set.
      if (s.kind === 'cockpit_state') {
        if (!Array.isArray(s.stateColumns) || s.stateColumns.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "source.kind='cockpit_state' requires a non-empty stateColumns allow-list (the columns the cockpit may write)",
            path: ['stateColumns'],
          });
        } else if (new Set(s.stateColumns).size !== s.stateColumns.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'source.stateColumns must not contain duplicate column names',
            path: ['stateColumns'],
          });
        }
      } else if (s.stateColumns !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "source.kind='tables' must not carry 'stateColumns'",
          path: ['stateColumns'],
        });
      }
    } else if (s.kind === 'action') {
      if (typeof s.actionId !== 'string' || s.actionId.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "source.kind='action' requires an actionId",
          path: ['actionId'],
        });
      }
      if (s.tableSlug !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "source.kind='action' must not carry a tableSlug",
          path: ['tableSlug'],
        });
      }
      if (s.stateColumns !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "source.kind='action' must not carry 'stateColumns'",
          path: ['stateColumns'],
        });
      }
      // Phase 3 action-target constraints. When actionKind is present we
      // enforce its target field; mcp_tool ⇒ toolSlug, workflow ⇒ workflowId.
      if (s.actionKind === 'mcp_tool') {
        if (typeof s.toolSlug !== 'string' || s.toolSlug.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "source.actionKind='mcp_tool' requires a toolSlug",
            path: ['toolSlug'],
          });
        }
        if (s.workflowId !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "source.actionKind='mcp_tool' must not carry a workflowId",
            path: ['workflowId'],
          });
        }
      } else if (s.actionKind === 'workflow') {
        if (typeof s.workflowId !== 'string' || s.workflowId.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "source.actionKind='workflow' requires a workflowId",
            path: ['workflowId'],
          });
        }
        if (s.toolSlug !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "source.actionKind='workflow' must not carry a toolSlug",
            path: ['toolSlug'],
          });
        }
      }
    } else {
      // Gateway kinds: library / mcp_tool / connection / workflow.
      // They reuse the binding's top-level toolSlug — forbid the
      // action-only fields (the local-read tableSlug + state allow-list too).
      for (const k of ['tableSlug', 'stateColumns', 'actionId', 'actionKind', 'toolSlug', 'workflowId', 'approvalGating', 'buttonLabel'] as const) {
        if (s[k] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `source.kind='${s.kind}' is a gateway read; it must not carry '${k}'`,
            path: [k],
          });
        }
      }
    }
  });
export type DashboardBindingSource = z.infer<
  typeof DashboardBindingSourceSchema
>;

// ─── Data binding descriptor (one row in data_bindings_jsonb) ─────────

/**
 * One declarative read binding the runtime is permitted to dispatch. The
 * runtime enforces:
 *   - Only bindings declared in this manifest are callable from the iframe.
 *   - Every call re-checks classification + RBAC at the gateway before
 *     dispatch (spec §4.3 + §9.2).
 *
 * Cockpit Phase 1 (§2.B): an OPTIONAL `source` discriminator generalizes
 * the bare `toolSlug` into a source union. A binding with no `source`
 * field — or a `source` whose kind is a gateway kind — still requires a
 * valid top-level `toolSlug` and dispatches identically to today. The
 * `tables`/`cockpit_state` kinds carry a `source.tableSlug` (local Tables
 * read, Phase 4) and the `action` kind carries a `source.actionId` (write
 * path, Phase 3) — neither requires a toolSlug.
 */
export const DashboardBindingSchema = z.object({
  /** Stable binding ID — the iframe uses this for fetch_via_mcp(bindingId). */
  id: z.string().min(1).max(120),

  /** Human-readable label. */
  name: z.string().min(1).max(200),

  /**
   * Cockpit Phase 1 source discriminator (optional). Absent = legacy
   * `mcp_tool` (toolSlug required). See DashboardBindingSourceSchema.
   */
  source: DashboardBindingSourceSchema.optional(),

  /**
   * Namespaced MCP tool slug. v1 vocabulary uses the 3-tier namespace from
   * spec §9 + CLAUDE.md §9.1: iq.*, ext.*, forge.*. Validation matches
   * the route layer's existing tool-slug regex.
   *
   * Cockpit Phase 1: now OPTIONAL at the field level — a `tables` /
   * `cockpit_state` / `action` binding does not carry one. The binding-
   * level `.refine()` re-requires it (present + regex-valid) whenever the
   * binding resolves to a gateway dispatch (no source, or a gateway-kind
   * source).
   */
  toolSlug: z
    .string()
    .min(1)
    .max(200)
    .regex(
      /^(iq|ext|forge)\.[a-zA-Z0-9_-]+\.[a-zA-Z_][a-zA-Z0-9_]*$/,
      'toolSlug must be a namespaced slug (e.g. iq.asset.list or ext.platform.getUsageStats)',
    )
    .optional(),

  /**
   * Parameter bag passed to the tool. Schema-validated at gateway dispatch
   * time against the tool's published JSONSchema. Stored verbatim here.
   */
  params: z.record(z.string(), z.unknown()).default({}),

  /**
   * Refresh policy for the binding's data.
   *   - 'manual'   — only fetches on explicit iframe request
   *   - 'mount'    — fetches once when the binding component mounts
   *   - 'interval' — fetches every refreshIntervalMs (subject to rate cap)
   *   - 'event'    — fetches when a host event matching eventTopic arrives
   *   - 'subscribe'— Sprint 11. Iframe opens an SSE channel on
   *                  /api/v1/argus-live/dashboards/:id/subscribe, scoped to
   *                  the `tables` slug list. Server pushes
   *                  `{ tableSlug, rowIds }` deltas; the iframe re-fetches
   *                  individual rows via the rows API (classification +
   *                  RLS still apply on the re-fetch). Long-poll fallback
   *                  via /poll is mounted alongside.
   *                  v1.1 amendment row 11 / Q12 — push row IDs only,
   *                  never inline data.
   */
  refreshMode: z.enum(['manual', 'mount', 'interval', 'event', 'subscribe']),

  /**
   * Required when refreshMode='interval'. Min 5 s, max 24 h, to keep
   * dashboards from hammering the MCP gateway. The runtime enforces
   * this cap before kicking the timer.
   */
  refreshIntervalMs: z
    .number()
    .int()
    .min(5_000)
    .max(86_400_000)
    .optional(),

  /**
   * Required when refreshMode='event'. Names the host topic the iframe
   * subscribes to (e.g. 'asset.changed', 'alarm.raised').
   */
  eventTopic: z.string().min(1).max(120).optional(),

  /**
   * Required when refreshMode='subscribe' (Sprint 11). The list of
   * `tenant_tables.slug` values the subscriber wants delta events for.
   * Server-side cap of 10 distinct slugs per subscription so a single
   * SSE channel can't deltas-flood across the whole tenant.
   *
   * Slug regex matches `tenant_tables.slug` — lowercase letters / digits /
   * underscores. Sysadmin canonical slugs (e.g. `workflow_run_summaries`)
   * and tenant-authored slugs share the same vocabulary.
   */
  tables: z
    .array(
      z
        .string()
        .min(1)
        .max(120)
        .regex(
          /^[a-z][a-z0-9_]*$/,
          'tables[*] must be a tenant_tables slug (lowercase, digits, underscores; starts with a letter)',
        ),
    )
    .min(1)
    .max(10)
    .optional(),

  /**
   * Parameter names the dashboard viewer may override at runtime. The
   * gateway still validates the final params against the tool schema.
   */
  userOverridableParams: z.array(z.string().min(1).max(120)).max(50).optional(),

  /**
   * Author-declared classification ceiling for this binding's response.
   * The gateway compares the actual tool response classification against
   * this hint and DENIES if the hint is lower (prevents author from
   * declaring a binding as 'unclassified' when the underlying tool returns
   * CUI data — defends against classification drift at publish time).
   */
  classificationHint: DashboardClassificationSchema.optional(),
})
  .refine(
    (b) => {
      // Cockpit Phase 1: a binding is valid iff it resolves to a usable
      // source.
      //   - No source OR a gateway-kind source → a present, regex-valid
      //     top-level toolSlug is REQUIRED (dispatch is via the gateway).
      //   - tables / cockpit_state / action source → toolSlug not required
      //     (the source carries tableSlug / actionId instead).
      // The toolSlug field's own .regex() already validates the format
      // WHEN present; this refine only governs PRESENCE.
      const needsToolSlug = !b.source || isGatewaySourceKind(b.source.kind);
      if (!needsToolSlug) return true;
      return typeof b.toolSlug === 'string' && b.toolSlug.length > 0;
    },
    {
      message:
        'A binding with no source (or a gateway-kind source: library/mcp_tool/connection/workflow) requires a top-level toolSlug',
      path: ['toolSlug'],
    },
  )
  .refine(
    (b) => b.refreshMode !== 'interval' || typeof b.refreshIntervalMs === 'number',
    {
      message: 'refreshMode=interval requires refreshIntervalMs',
      path: ['refreshIntervalMs'],
    },
  )
  .refine(
    (b) => b.refreshMode !== 'event' || (typeof b.eventTopic === 'string' && b.eventTopic.length > 0),
    {
      message: 'refreshMode=event requires eventTopic',
      path: ['eventTopic'],
    },
  )
  .refine(
    (b) => b.refreshMode !== 'subscribe' || (Array.isArray(b.tables) && b.tables.length > 0),
    {
      message: 'refreshMode=subscribe requires tables[] with 1-10 tenant_tables slugs',
      path: ['tables'],
    },
  );
export type DashboardBinding = z.infer<typeof DashboardBindingSchema>;

/** Manifest = ordered array of bindings. Stored as data_bindings_jsonb. */
export const DashboardBindingManifestSchema = z.array(DashboardBindingSchema);
export type DashboardBindingManifest = z.infer<
  typeof DashboardBindingManifestSchema
>;

/**
 * Cockpit Phase 4 (STATE) — resolve the WRITE allow-list a published cockpit
 * declared for a given own-state table slug. Scans the raw `data_bindings_jsonb`
 * (untrusted shape — the manifest is stored as jsonb) for a `cockpit_state`
 * source whose `tableSlug` matches, and returns its `stateColumns`.
 *
 * Used by the STATE route to enforce, on every WRITE_STATE, that the iframe can
 * ONLY write (a) into a table the cockpit actually declared as `cockpit_state`,
 * and (b) the columns the author allow-listed.
 *
 * Returns:
 *   - `{ declared: true, columns }` — a matching cockpit_state binding exists.
 *   - `{ declared: false }`         — no cockpit_state binding names this slug
 *                                     (the write must be refused: a cockpit may
 *                                     only write its OWN declared state table).
 *
 * Defensive: tolerates a manifest entry that is not an object / lacks a source,
 * and a stateColumns value that is not a clean string[] (drops non-strings).
 */
export function resolveCockpitStateColumns(
  dataBindings: unknown,
  tableSlug: string,
): { declared: true; columns: string[] } | { declared: false } {
  if (!Array.isArray(dataBindings)) return { declared: false };
  for (const entry of dataBindings) {
    if (!entry || typeof entry !== 'object') continue;
    const src = (entry as { source?: unknown }).source;
    if (!src || typeof src !== 'object') continue;
    const s = src as { kind?: unknown; tableSlug?: unknown; stateColumns?: unknown };
    if (s.kind !== 'cockpit_state') continue;
    if (s.tableSlug !== tableSlug) continue;
    const columns = Array.isArray(s.stateColumns)
      ? s.stateColumns.filter((c): c is string => typeof c === 'string' && c.length > 0)
      : [];
    return { declared: true, columns };
  }
  return { declared: false };
}

// ─── Sandbox config (sandbox_config_jsonb) ─────────────────────────────

/**
 * Per-dashboard sandbox knobs. Authoritative CSP comes from the runtime
 * layer (AL-A3) — these fields can only TIGHTEN, never loosen.
 */
export const DashboardSandboxConfigSchema = z.object({
  /** Iframe dimensions. */
  dimensions: z
    .object({
      width: z.union([z.literal('full'), z.number().int().min(120).max(2400)]),
      height: z.number().int().min(120).max(4800),
    })
    .optional(),

  /** Theme override. NULL = inherit tenant theme. */
  theme: z.enum(['inherit', 'light', 'dark']).default('inherit'),

  /**
   * Optional whole-dashboard auto-refresh (seconds). Distinct from
   * per-binding refreshIntervalMs above; this drives a full re-render.
   * Min 30 s to keep this from being a DOS vector.
   */
  autoRefreshSeconds: z.number().int().min(30).max(3600).optional(),

  /**
   * CSP extras applied IN ADDITION to the runtime's base CSP. Only
   * directives that further restrict are honored (the runtime layer
   * filters out loosening attempts; AL-A3 enforces).
   */
  cspExtras: z.record(z.string(), z.array(z.string())).optional(),
});
export type DashboardSandboxConfig = z.infer<
  typeof DashboardSandboxConfigSchema
>;

// ─── Architect review (architect_review_jsonb) ─────────────────────────

export const DashboardArchitectFindingSchema = z.object({
  code: z.string().min(1).max(80),
  severity: z.enum(['info', 'warn', 'error']),
  message: z.string().min(1).max(2000),
  /** Optional pointer into code_html/code_js/code_css. */
  location: z
    .object({
      file: z.enum(['html', 'js', 'css', 'binding', 'sandbox']),
      line: z.number().int().nonnegative().optional(),
    })
    .optional(),
});
export type DashboardArchitectFinding = z.infer<
  typeof DashboardArchitectFindingSchema
>;

export const DashboardArchitectReviewSchema = z.object({
  findings: z.array(DashboardArchitectFindingSchema).default([]),
  /** Aggregate verdict; controls whether publish is allowed. */
  severity: z.enum(['pass', 'warn', 'block']),
  /** True when an authorised admin has signed off despite warnings. */
  signedOff: z.boolean(),
  /** ISO-8601 timestamp of the review. */
  reviewedAt: z.string().datetime(),
  /** UUID of the architect agent that produced the review. */
  reviewerAgentId: z.string().uuid().optional(),
});
export type DashboardArchitectReview = z.infer<
  typeof DashboardArchitectReviewSchema
>;

// ─── Dashboard row (response shape) ────────────────────────────────────

/** Slug pattern: lowercased ASCII letters/digits/hyphens, 1-120 chars. */
export const DashboardSlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?$/,
    'slug must be lowercase a-z0-9 with optional hyphens (start + end with alnum)',
  );

export const TenantDashboardSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  ownerUserId: z.string().uuid(),
  slug: DashboardSlugSchema,
  title: z.string().min(1).max(200),
  description: z.string().nullable(),
  icon: z.string().max(60).nullable(),
  status: DashboardStatusSchema,
  enabled: z.boolean(),
  currentVersionId: z.string().uuid().nullable(),
  schemaVersion: z.number().int().min(1),
  classificationTag: DashboardClassificationSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  deletedAt: z.string().datetime().nullable(),
});
export type TenantDashboardDto = z.infer<typeof TenantDashboardSchema>;

// ─── Version row (response shape) ──────────────────────────────────────

export const TenantDashboardVersionSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  dashboardId: z.string().uuid(),
  revLabel: z.string().max(40),
  revSequence: z.number().int().min(1),
  codeHtml: z.string(),
  codeJs: z.string(),
  codeCss: z.string(),
  dataBindingsJsonb: DashboardBindingManifestSchema,
  sandboxConfigJsonb: DashboardSandboxConfigSchema,
  classificationTag: DashboardClassificationSchema,
  contentHash: z.string().min(1).max(128),
  authoredByUserId: z.string().uuid(),
  authoredViaAgentId: z.string().uuid().nullable(),
  authoredViaConversationId: z.string().uuid().nullable(),
  architectReviewJsonb: DashboardArchitectReviewSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type TenantDashboardVersionDto = z.infer<
  typeof TenantDashboardVersionSchema
>;

// ─── Grant row (response shape) ────────────────────────────────────────

/**
 * Grant rows enforce a row-level XOR on the three grantee columns. The
 * Zod refinement mirrors the DB CHECK constraint
 * tenant_dashboard_grants_xor.
 */
export const TenantDashboardGrantSchema = z
  .object({
    id: z.string().uuid(),
    orgId: z.string().uuid(),
    dashboardId: z.string().uuid(),
    granteeUserId: z.string().uuid().nullable(),
    granteeGroupId: z.string().uuid().nullable(),
    granteeRole: DashboardGrantTenantRoleSchema.nullable(),
    role: DashboardGrantAccessSchema,
    grantedByUserId: z.string().uuid(),
    grantedAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
  })
  .refine(
    (g) => {
      const n =
        (g.granteeUserId !== null ? 1 : 0) +
        (g.granteeGroupId !== null ? 1 : 0) +
        (g.granteeRole !== null ? 1 : 0);
      return n === 1;
    },
    {
      message:
        'Exactly one of granteeUserId, granteeGroupId, granteeRole must be set',
    },
  );
export type TenantDashboardGrantDto = z.infer<
  typeof TenantDashboardGrantSchema
>;

// ─── Audit event types (spec §3.4 — for event_type validation) ─────────

/**
 * Canonical Argus Live event_type vocabulary for audit_chain rows.
 * The audit emitter (AL-A2) MUST use these exact strings so the
 * retention worker's high-volume aggregation matcher (Q4) stays stable.
 *
 * High-volume types (subject to commercial-tenant aggregation after
 * 90 days): 'dashboard.viewed', 'dashboard.binding.fetched'.
 */
export const DashboardAuditEventTypeSchema = z.enum([
  'dashboard.created',
  'dashboard.rev.created',
  'dashboard.published',
  'dashboard.archived',
  'dashboard.viewed',
  'dashboard.binding.fetched',
  'dashboard.binding.denied',
  'dashboard.sandbox.violation',
  'dashboard.runtime.crashed',
  'dashboard.runtime.throttled',
  'dashboard.grant.added',
  'dashboard.grant.removed',
  'dashboard.exported',
  'dashboard.imported',
]);
export type DashboardAuditEventType = z.infer<
  typeof DashboardAuditEventTypeSchema
>;

/**
 * High-volume event types eligible for commercial-tenant aggregation
 * after 90 days (spec §11 Q4). The retention worker uses this set as
 * its matcher.
 */
export const HIGH_VOLUME_DASHBOARD_EVENT_TYPES = [
  'dashboard.viewed',
  'dashboard.binding.fetched',
] as const satisfies readonly DashboardAuditEventType[];
